'use client';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import throttle from 'lodash/throttle';

const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

// Skip processing while a modal has locked scroll, so the layout shift from
// opening/closing it isn't misread as scrolling.
const isScrollLocked = () =>
  Boolean(document.body.dataset.scrollLocked) ||
  document.body.classList.contains('ReactModal__Body--open');

export const pageScrolled = percentageScrolled => ({
  type: '[View] Page Scrolled',
  data: { perc_scrolled: percentageScrolled },
});

export const usePageScrollDepthTracking = reporter => {
  const pathname = usePathname();
  const reachedMilestonesRef = useRef(new Set());
  //reporterRef ensures events still run if there is a rerender while
  //not at the top of the page
  const reporterRef = useRef(reporter);
  reporterRef.current = reporter;

  useEffect(() => {
    reachedMilestonesRef.current = new Set();

    // Account pages are excluded from scroll-depth tracking for now.
    if (pathname?.includes('account')) {
      return undefined;
    }

    //Only run when landing at the top of the page
    //prevents events from rerunning after refresh
    if (window.scrollY !== 0) {
      return undefined;
    }

    const checkScrollDepth = () => {
      if (isScrollLocked()) return;

      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return;

      const percentageScrolled = Math.min(
        100,
        Math.round((window.scrollY / maxScroll) * 100),
      );

      SCROLL_MILESTONES.forEach(milestone => {
        if (
          percentageScrolled >= milestone &&
          !reachedMilestonesRef.current.has(milestone)
        ) {
          reachedMilestonesRef.current.add(milestone);
          reporterRef.current.tag(pageScrolled(milestone));
        }
      });
    };

    const throttledCheck = throttle(checkScrollDepth, 250);
    window.addEventListener('scroll', throttledCheck, { passive: true });
    window.addEventListener('resize', throttledCheck, { passive: true });

    return () => {
      throttledCheck.cancel();
      window.removeEventListener('scroll', throttledCheck);
      window.removeEventListener('resize', throttledCheck);
    };
  }, [pathname]);
};
